import { StandardEvents } from '@shopify/events';
import { isCartBusy, updateLines } from '@theme/uradora-cart';

const SOURCE = 'uradora-cart-boxes';

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/** Resolves after the next paint, once a re-render from the same cart change has landed. */
const afterPaint = () => new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

/** How long the stepper waits after the last tap before saving, so quick taps go in as one change. */
const SAVE_DELAY = 450;

/** @param {string | undefined} list - Comma-separated line keys */
const keysOf = (list) => (list ?? '').split(',').filter(Boolean);

/** @param {HTMLInputElement} input */
function limitsOf(input) {
  const min = Number(input.min) || 1;
  return { min, max: Math.max(Number(input.max) || Infinity, min), step: Number(input.step) || 1 };
}

/**
 * Runs the quantity steppers and remove buttons on the cart's box cards
 * (snippets/uradora-cart-boxes.liquid). A box's fillings and personal touch always match its
 * quantity, so a change updates them in the same request, and removing a box removes
 * everything nested under it. The drawer and cart page re-render from the response.
 * Boxes are listed oldest first, so in the drawer a newly added box is scrolled into view.
 */
class UradoraCartBoxes extends HTMLElement {
  /** @type {Map<HTMLElement, ReturnType<typeof setTimeout>>} */
  #timers = new Map();

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('input', this.#onInput);
    this.addEventListener('change', this.#onChange);
    this.addEventListener('keydown', this.#onKeydown);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('input', this.#onInput);
    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('keydown', this.#onKeydown);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate);

    // Save anything still waiting rather than dropping it.
    for (const card of [...this.#timers.keys()]) this.#saveQuantity(card);
  }

  /**
   * After Add to Cart, scrolls the drawer to the new box, which joins the end of the list.
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #onCartLinesUpdate = (event) => {
    const drawer = this.closest('.ucart-drawer');
    if (event.action !== 'add' || !drawer) return;

    const cards = () => /** @type {HTMLElement[]} */ ([...drawer.querySelectorAll('[data-box]')]);
    const known = new Set(cards().map((card) => card.dataset.key));

    event.promise
      ?.then(afterPaint)
      .then(() => {
        const added = cards().find((card) => !known.has(card.dataset.key));
        added?.scrollIntoView({ block: 'nearest', behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      })
      .catch(() => {});
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const control = event.target instanceof Element ? event.target.closest('[data-step], [data-remove]') : null;
    const card = control?.closest('[data-box]');
    if (!(control instanceof HTMLElement) || !(card instanceof HTMLElement) || isCartBusy()) return;

    if (control.hasAttribute('data-remove')) {
      this.#cancel(card);
      this.#save(card, 0);
      return;
    }

    const input = this.#inputOf(card);
    if (!input) return;

    const { min, max, step } = limitsOf(input);
    const next = (Number(input.value) || min) + Number(control.dataset.step) * step;

    if (next > max) {
      this.#flagLimit(card, input);
      return;
    }
    if (next < min) return;

    input.value = String(next);
    this.#clearMessage(card);
    this.#refresh(card);
    this.#schedule(card);
  };

  /** Stops the quantity at the limit as the customer types, saying why. */
  #onInput = (/** @type {Event} */ event) => {
    const input = event.target;
    const card = input instanceof HTMLInputElement ? input.closest('[data-box]') : null;
    if (!(input instanceof HTMLInputElement) || !(card instanceof HTMLElement)) return;

    this.#cancel(card);
    const { max } = limitsOf(input);
    if (input.value !== '' && Number(input.value) > max) {
      input.value = String(max);
      this.#flagLimit(card, input);
    }
    this.#refresh(card);
  };

  /** Saves a typed quantity once the field is left. */
  #onChange = (/** @type {Event} */ event) => {
    const input = event.target;
    const card = input instanceof HTMLInputElement ? input.closest('[data-box]') : null;
    if (input instanceof HTMLInputElement && card instanceof HTMLElement) this.#saveQuantity(card);
  };

  /** Enter saves a typed quantity straight away. */
  #onKeydown = (/** @type {KeyboardEvent} */ event) => {
    const input = event.target;
    if (event.key !== 'Enter' || !(input instanceof HTMLInputElement)) return;

    const card = input.closest('[data-box]');
    if (!(card instanceof HTMLElement)) return;

    event.preventDefault();
    this.#saveQuantity(card);
  };

  /** @param {HTMLElement} card */
  #inputOf(card) {
    const input = card.querySelector('[data-quantity-input]');
    return input instanceof HTMLInputElement ? input : null;
  }

  /** @param {HTMLElement} card */
  #schedule(card) {
    this.#cancel(card);
    this.#timers.set(
      card,
      setTimeout(() => this.#saveQuantity(card), SAVE_DELAY)
    );
  }

  /** @param {HTMLElement} card */
  #cancel(card) {
    clearTimeout(this.#timers.get(card));
    this.#timers.delete(card);
  }

  /**
   * Settles the stepper on a whole number within its limits and saves it if it changed.
   * @param {HTMLElement} card
   */
  #saveQuantity(card) {
    this.#cancel(card);
    const input = this.#inputOf(card);
    if (!input) return;

    const { min, max, step } = limitsOf(input);
    const typed = Math.floor(Number(input.value));
    const quantity = Number.isFinite(typed) && typed >= min ? Math.min(min + Math.round((typed - min) / step) * step, max) : min;

    input.value = String(quantity);
    this.#refresh(card);
    if (quantity !== Number(input.defaultValue)) this.#save(card, quantity);
  }

  /**
   * Sets the box to this quantity along with the lines that follow it, or removes the box and
   * everything nested under it when the quantity is zero.
   * @param {HTMLElement} card
   * @param {number} quantity
   */
  async #save(card, quantity) {
    const key = card.dataset.key;
    if (!key) return;

    /** @type {Record<string, number>} */
    const updates = { [key]: quantity };
    const nested = quantity === 0 ? keysOf(card.dataset.nested) : keysOf(card.dataset.followers);
    for (const line of nested) updates[line] = quantity;

    this.#clearMessage(card);
    // A detached element's events never reach the document, where the cart listens.
    const result = await updateLines(updates, { source: SOURCE, target: this.isConnected ? this : document });
    if (!result.status) return;

    const input = this.#inputOf(card);
    if (input) input.value = input.defaultValue;
    this.#refresh(card);
    this.#showMessage(card, result.description || this.dataset.error || '');
  }

  /**
   * Greys out a stepper button that can't go further.
   * @param {HTMLElement} card
   */
  #refresh(card) {
    const input = this.#inputOf(card);
    if (!input) return;

    const { min, max } = limitsOf(input);
    const value = Number(input.value);
    for (const button of card.querySelectorAll('[data-step]')) {
      const blocked = Number(/** @type {HTMLElement} */ (button).dataset.step) < 0 ? value <= min : value >= max;
      button.setAttribute('aria-disabled', String(blocked));
    }
  }

  /**
   * Explains why the box can't go higher: stock, or the most we take online.
   * @param {HTMLElement} card
   * @param {HTMLInputElement} input
   */
  #flagLimit(card, input) {
    const template = input.dataset.stockLimited === 'true' ? this.dataset.stockLimit : this.dataset.orderLimit;
    this.#showMessage(card, (template ?? '').replace('[limit]', String(limitsOf(input).max)));
  }

  /**
   * @param {HTMLElement} card
   * @param {string} text
   */
  #showMessage(card, text) {
    const message = card.querySelector('[data-box-error]');
    if (!(message instanceof HTMLElement) || !text) return;

    message.textContent = text;
    message.hidden = false;
    message.classList.remove('is-flagged');
    void message.offsetWidth;
    message.classList.add('is-flagged');
  }

  /** @param {HTMLElement} card */
  #clearMessage(card) {
    const message = card.querySelector('[data-box-error]');
    if (!(message instanceof HTMLElement)) return;

    message.hidden = true;
    message.textContent = '';
  }
}

if (!customElements.get('uradora-cart-boxes')) {
  customElements.define('uradora-cart-boxes', UradoraCartBoxes);
}
